import React from 'react'
import { useSelector } from 'react-redux'

import { Field, formValueSelector } from 'redux-form/immutable'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import PrimaryButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/primaryButton/PrimaryButton'
import { InputFieldRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { ToggleSwitchRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/toggleSwitch/ToggleSwitch'
import { colors } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/colors'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

// TODO!!! UPDATE SO WE DYNAMICALLY BUILD THE UPDATE FORM TO MATCH PROVIDER MODALITY.
const ProviderRatesForm: React.FC<ProviderRatesFormProps> = ({
  handleSubmit,
  submitting,
  submitFunction,
  primaryButtonText,
}) => {
  const { isTimeBasedDABillingEnabled } = useFlags()
  const selector = formValueSelector('editProviderRatesForm')
  const differentialPricingEnabled = useSelector((state: boolean) => selector(state, 'differential_pricing_enabled'))
  return (
    <form onSubmit={handleSubmit(submitFunction)}>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='individual_initial_evaluation'
            testID={tID('individual_initial_evaluation')}
            component={InputFieldRFF}
            label='Initial Evaluation (individual) Rate'
          />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='individual_follow_up_session'
            testID={tID('individual_follow_up_session')}
            component={InputFieldRFF}
            label='Follow Up Session (individual) Rate'
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='couples_initial_evaluation'
            testID={tID('couples_initial_evaluation')}
            component={InputFieldRFF}
            label='Initial Evaluation (couples) Rate'
          />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='couples_follow_up_session'
            testID={tID('couples_follow_up_session')}
            component={InputFieldRFF}
            label='Follow Up Session (couples) Rate'
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='family_initial_evaluation'
            testID={tID('family_initial_evaluation')}
            component={InputFieldRFF}
            label='Initial Evaluation (family) Rate'
          />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='family_follow_up_session'
            testID={tID('family_follow_up_session')}
            component={InputFieldRFF}
            label='Follow Up Session (family) Rate'
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='group_initial_evaluation'
            testID={tID('group_initial_evaluation')}
            component={InputFieldRFF}
            label='Initial Evaluation (group) Rate'
          />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='group_follow_up_session'
            testID={tID('group_follow_up_session')}
            component={InputFieldRFF}
            label='Follow Up Session (group) Rate'
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='medication_management_initial_evaluation'
            testID={tID('medication_management_initial_evaluatio')}
            component={InputFieldRFF}
            label={
              isTimeBasedDABillingEnabled
                ? 'Med Management Initial Evaluation'
                : 'Initial Evaluation (medication management) Rate'
            }
          />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='medication_management_follow_up_session'
            testID={tID('medication_management_follow_up_session')}
            component={InputFieldRFF}
            label={
              isTimeBasedDABillingEnabled
                ? 'Med Management Follow-Up (Legacy)'
                : 'Follow Up Session (medication management) Rate'
            }
          />
        </div>
      </div>
      {isTimeBasedDABillingEnabled && (
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='medication_management_follow_up_low_complexity'
              testID={tID('medication_management_follow_up_low_complexity')}
              component={InputFieldRFF}
              label='Med Management Low Complexity Follow-Up (20-29min)'
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='medication_management_follow_up_moderate_complexity'
              testID={tID('medication_management_follow_up_moderate_complexity')}
              component={InputFieldRFF}
              label='Med Management Moderate Complexity Follow-Up (30-39min)'
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='medication_management_follow_up_high_complexity'
              testID={tID('medication_management_follow_up_high_complexity')}
              component={InputFieldRFF}
              label='Med Management High Complexity Follow-Up (40+min)'
            />
          </div>
        </div>
      )}
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field name='smp_session' testID={tID('smp_session')} component={InputFieldRFF} label='SMP Session Rate' />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <Field name='ssc_session' testID={tID('ssc_session')} component={InputFieldRFF} label='GSC Session Rate' />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='child_initial_evaluation'
            testID={tID('child_initial_evaluation')}
            component={InputFieldRFF}
            label='Initial Evaluation (minor) Rate'
          />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='child_follow_up_session'
            testID={tID('child_follow_up_session')}
            component={InputFieldRFF}
            label='Follow Up Session (minor) Rate'
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='minor_medication_management_initial_evaluation'
            testID={tID('minor_medication_management_initial_evaluation')}
            component={InputFieldRFF}
            label={
              isTimeBasedDABillingEnabled ? 'Minor Med Management Initial Evaluation' : 'Minor Intake (prescriber) Rate'
            }
          />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='minor_medication_management_follow_up_evaluation'
            testID={tID('minor_medication_management_follow_up_evaluation')}
            component={InputFieldRFF}
            label={
              isTimeBasedDABillingEnabled
                ? 'Minor Med Management Follow-Up (Legacy)'
                : 'Minor Follow Up (prescriber) Rate'
            }
          />
        </div>
      </div>
      {isTimeBasedDABillingEnabled && (
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='minor_medication_management_follow_up_low_complexity'
              testID={tID('minor_medication_management_follow_up_low_complexity')}
              component={InputFieldRFF}
              label='Minor Med Management Low Complexity Follow-Up (20-29min)'
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='minor_medication_management_follow_up_moderate_complexity'
              testID={tID('minor_medication_management_follow_up_moderate_complexity')}
              component={InputFieldRFF}
              label='Minor Med Management Moderate Complexity Follow-Up (30-39min)'
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='minor_medication_management_follow_up_high_complexity'
              testID={tID('minor_medication_management_follow_up_high_complexity')}
              component={InputFieldRFF}
              label='Minor Med Management High Complexity Follow-Up (40+min)'
            />
          </div>
        </div>
      )}

      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='alcohol_use_disorder_session'
            testID={tID('alcohol_use_disorder_session')}
            component={InputFieldRFF}
            label='Alcohol Use Disorder Session Therapy Rate'
          />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <Field name='dbt_session' testID={tID('dbt_session')} component={InputFieldRFF} label='DBT Session Rate' />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='dbt_group_session'
            testID={tID('dbt_group_session')}
            component={InputFieldRFF}
            label='DBT Group Session Rate'
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='dbt_teens_intake_session'
            testID={tID('dbt_teens_intake_session')}
            component={InputFieldRFF}
            label='DBT Teens Intake Session Rate'
          />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='dbt_teens_followup_session'
            testID={tID('dbt_teens_followup_session')}
            component={InputFieldRFF}
            label='DBT Teens Follow-Up Session Rate'
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='dbt_teens_caregiver_session'
            testID={tID('dbt_teens_caregiver_session')}
            component={InputFieldRFF}
            label='DBT Teens Caregiver Session Rate'
          />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='dbt_teens_family_session'
            testID={tID('dbt_teens_family_session')}
            component={InputFieldRFF}
            label='DBT Teens Family Session Rate'
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='dbt_teens_group_session'
            testID={tID('dbt_teens_group_session')}
            component={InputFieldRFF}
            label='DBT Teens Group Session Rate'
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='dbt_teens_medication_initial_session'
            testID={tID('dbt_teens_medication_initial_session')}
            component={InputFieldRFF}
            label='DBT Teens Medication Initial Session Rate'
          />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='dbt_teens_medication_followup_session'
            testID={tID('dbt_teens_medication_followup_session')}
            component={InputFieldRFF}
            label='DBT Teens Medication Follow-Up Session Rate'
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='caregiver_training_for_neuro_session'
            testID={tID('caregiver_training_for_neuro_session')}
            component={InputFieldRFF}
            label='Caregiver Training Session Rate'
          />
        </div>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='neurodiversity_skills_coaching_session'
            testID={tID('neurodiversity_skills_coaching_session')}
            component={InputFieldRFF}
            label='Neurodiversity Skills Coaching Session Rate'
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <Field
            name='manager_coaching_session'
            testID={tID('manager_coaching_session')}
            component={InputFieldRFF}
            label='Manager Growth Coaching Session Rate'
          />
        </div>
      </div>

      <hr />
      <div className='row'>
        <div className='col-xs-12 col-sm-6'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              component={ToggleSwitchRFF}
              label='Differential Pricing'
              name='differential_pricing_enabled'
              labelColor={colors.mediumGrayBlue}
              offColor={colors.mediumGrayBlue}
              onColor={colors.devBlue}
              handleColor={colors.charcoal1}
            />
          </div>
        </div>

        {differentialPricingEnabled && (
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='differential_pricing_in_person_modifier'
              testID={tID('differential_pricing_in_person_modifier')}
              component={InputFieldRFF}
              label='In Person Modifier'
            />
          </div>
        )}
      </div>

      <div className='row'>
        <div className='col-xs-12'>
          <PrimaryButton type='submit' id='test-providerrates-submit' isLoading={submitting}>
            {primaryButtonText}
          </PrimaryButton>
        </div>
      </div>
    </form>
  )
}

type ProviderRatesFormProps = {
  handleSubmit: (submit: (values: any) => unknown) => (event?: React.SyntheticEvent) => void
  submitting: boolean
  submitFunction: (values: any) => unknown
  primaryButtonText: string
}

export default ProviderRatesForm
